/**
 * Video Stream Types for Bambu Lab Printers
 *
 * These types define the video streaming capabilities and configuration
 * for Bambu Lab 3D printers.
 */

/**
 * Video stream types supported by Bambu Lab printers
 */
export enum VideoStreamType {
  /** Direct RTSP stream on local network */
  LOCAL_RTSP = 'local_rtsp',
  /** Remote stream through Bambu cloud (TUTK protocol) */
  REMOTE_TUTK = 'remote_tutk',
  /** Remote stream through Bambu cloud (Agora protocol) */
  REMOTE_AGORA = 'remote_agora',
  /** No streaming available */
  NONE = 'none',
}

/**
 * Video stream quality settings
 */
export enum VideoStreamQuality {
  /** 1920x1080 */
  HD_1080P = '1080p',
  /** 1280x720 */
  HD_720P = '720p',
  /** 640x480 */
  SD_480P = '480p',
}

/**
 * Video stream connection status
 */
export enum VideoStreamStatus {
  /** Stream is not initialized */
  IDLE = 'idle',
  /** Stream is connecting */
  CONNECTING = 'connecting',
  /** Stream is connected and active */
  ACTIVE = 'active',
  /** Stream connection failed */
  ERROR = 'error',
  /** Stream was disconnected */
  DISCONNECTED = 'disconnected',
}

/**
 * Video stream information
 */
export interface VideoStreamInfo {
  /** Stream type */
  type: VideoStreamType;
  /** Stream URL */
  url: string;
  /** Stream quality */
  quality: VideoStreamQuality;
  /** Stream status */
  status: VideoStreamStatus;
  /** Printer ID */
  printerId: string;
  /** Printer name */
  printerName: string;
  /** Printer model */
  printerModel: string;
  /** Camera index (for multi-camera printers like H2S/H2D) */
  cameraIndex?: number;
  /** Last connection timestamp */
  lastConnected?: string;
  /** Last disconnection timestamp */
  lastDisconnected?: string;
  /** Error message if stream failed */
  error?: string;
  /** Additional metadata */
  metadata?: Record<string, any>;
}

/**
 * Video stream configuration
 */
export interface VideoStreamConfig {
  /** Printer IP address */
  host: string;
  /** Printer access token (used as password for RTSP) */
  accessToken: string;
  /** RTSP username (default: 'bblp') */
  username?: string;
  /** RTSP URL from printer state (from MQTT ipcam.rtsp_url) - if available, use this instead of generating */
  rtspUrl?: string;
  /** Preferred stream quality */
  quality?: VideoStreamQuality;
  /** Preferred stream type */
  preferredType?: VideoStreamType;
  /** Connection timeout in ms */
  timeout?: number;
  /** Camera index (for multi-camera printers) */
  cameraIndex?: number;
  /** Enable automatic reconnection */
  autoReconnect?: boolean;
}

/**
 * Video stream capabilities for a printer model
 */
export interface VideoStreamCapabilities {
  /** Whether this model has a camera */
  hasCamera: boolean;
  /** Whether this model supports multiple cameras */
  multiCamera: boolean;
  /** Number of cameras available */
  cameraCount: number;
  /** Supported stream types */
  supportedTypes: VideoStreamType[];
  /** Supported resolutions */
  supportedResolutions: VideoStreamQuality[];
  /** Default RTSP port */
  rtspPort: number;
  /** Default stream quality */
  defaultQuality: VideoStreamQuality;
}

/**
 * Video stream request
 */
export interface VideoStreamRequest {
  /** Printer ID */
  printerId: string;
  /** Desired stream type */
  type?: VideoStreamType;
  /** Desired quality */
  quality?: VideoStreamQuality;
  /** Camera index (for multi-camera printers) */
  cameraIndex?: number;
}

/**
 * Video stream response
 */
export interface VideoStreamResponse {
  /** Whether the request was successful */
  success: boolean;
  /** Stream information (if successful) */
  stream?: VideoStreamInfo;
  /** Error message (if failed) */
  error?: string;
  /** Error code (if failed) */
  errorCode?: string;
}

/**
 * Camera control command types
 */
export enum CameraControlCommand {
  /** Enable/disable recording */
  RECORDING = 'ipcam_record_set',
  /** Enable/disable timelapse */
  TIMELAPSE = 'ipcam_timelapse',
  /** Set resolution */
  RESOLUTION = 'ipcam_resolution_set',
}

/**
 * Camera control request
 */
export interface CameraControlRequest {
  /** Command type */
  command: CameraControlCommand;
  /** Control value (enable/disable) */
  control?: 'enable' | 'disable';
  /** Resolution value (for resolution command) */
  resolution?: VideoStreamQuality;
}

/**
 * Camera status information
 */
export interface CameraStatus {
  /** Whether camera exists on the printer */
  exists: boolean;
  /** Whether recording is enabled */
  recording: boolean;
  /** Whether recording during print is enabled */
  recordingDuringPrint: boolean;
  /** Whether timelapse is enabled */
  timelapse: boolean;
  /** Current resolution */
  resolution: VideoStreamQuality;
  /** Supported resolutions */
  supportedResolutions: VideoStreamQuality[];
  /** Whether camera is currently busy */
  busy: boolean;
  /** Virtual camera status (if applicable) */
  virtualCamera?: boolean;
}
